// The cache layer as the rest of the browser sees it.
//
// It sits inside src/web/main.ts's `invoke`, which is the seam the whole web build
// is already organised around: the renderer talks to `window.api` and cannot tell
// what is behind it. So Terminal.tsx, the ~96 other api() call sites, and every
// desktop and mobile build are untouched by any of this.
//
// Two jobs:
//   READS  — answered from the catalog cache, revalidated in the background, with
//            changes announced through the same onDataChanged fan-out the SSE
//            stream already uses, so useDataChanged does the re-render as before.
//   CHARGE — priced locally, given a pre-claimed receipt number, written to a
//            durable queue and returned immediately. The cashier waits on nothing.

import type {
  CashSession,
  CashSessionDetail,
  CatalogSnapshot,
  CheckoutInput,
  ProductDetail,
  ProductVariant,
  SaleDetail,
  SaleItemDetail,
  SessionUser,
  SyncStatus
} from '../../shared/types'
import type { DataTopic, IpcChannel, IpcContract } from '../../shared/ipc-contract'
import { priceCart, assertStock, type CatalogLookup } from '../../core/services/checkout-calc'
import * as catalog from './catalog'
import * as queue from './queue'
import { startDrain, type Drain } from './drain'
import { applyPendingStock, filterCategories, filterProducts, type CatalogState } from './merge'

/** The raw transport — a real HTTP round trip, with no cache in the way. */
export type Post = <K extends IpcChannel>(
  channel: K,
  req: IpcContract[K]['req']
) => Promise<IpcContract[K]['res']>

/** How many receipt numbers to claim at a time, and when to top up. */
const RECEIPT_BLOCK = 50
const RECEIPT_REFILL_AT = 20

/** Reads served from the catalog cache. Everything else goes straight to the server. */
const CACHED_READS = new Set<string>([
  'products:list',
  'products:get',
  'categories:list',
  'settings:get',
  'storeProfile:get',
  'locations:list'
])

/** Topics whose arrival means the catalog cache may be out of date. */
const CATALOG_TOPICS = new Set<DataTopic>(['products', 'categories', 'settings', 'locations'])

let post: Post | null = null
let session: SessionUser | null = null
let drain: Drain | null = null
/** The browser's view of its open drawer, kept from every cash:* response. */
let cashSession: CashSession | null = null
/** Whether any cash:* response has told us the drawer's state yet (an open drawer OR none). */
let drawerKnown = false
let fanout: ((topics: DataTopic[]) => void) | null = null

/**
 * Responses to `products:get`, cached as they arrive rather than reconstructed.
 *
 * A ProductDetail carries description, images, options, attributes and tags — none
 * of which the catalog snapshot holds, and inventing empty ones would quietly break
 * the product editor. So the real response is kept, and only the fields that go
 * stale fast (price, stock, the variant list) are overlaid from the catalog cache,
 * which is delta-synced. First open costs a round trip; the variant picker never
 * pays it again.
 */
const details = new Map<string, ProductDetail>()

function ns(): string | null {
  return session ? queue.namespaceOf(session.storeId, session.id) : null
}

const fetchSnapshot = (since?: string): Promise<CatalogSnapshot> => {
  if (!post) throw new Error('cache not installed')
  return post('catalog:sync', since ? { since } : {})
}

// ---------- session lifecycle ----------

/**
 * Learn who we are from a response that already says so.
 *
 * `auth:session` and `auth:login` both return a SessionUser carrying the store and
 * the POS user, so the cache never has to ask — an extra round trip to find out
 * where to cache would defeat the point.
 */
export async function observeSession(next: SessionUser | null): Promise<void> {
  const before = ns()
  session = next
  if (!next) return
  if (ns() === before) return
  details.clear()
  await catalog.bind(next.storeId)
  await queue.load()
  await queue.loadReceipts(ns()!)
  void topUpReceipts()
  drain?.kick()
  void catchUp()
}

/**
 * One revalidate as soon as we know who we are.
 *
 * Without it a browser that was CLOSED while the shop carried on would serve
 * yesterday's prices indefinitely: the SSE stream only reports what happens while
 * it is connected, and its first `ready` after a fresh connect is not a reconnect,
 * so it invalidates nothing. Every other refresh path is an event we were present
 * for; this is the one that covers the time we were not.
 *
 * Background, deliberately. The cache answers from disk immediately and this
 * corrects it a moment later through the normal fan-out — making the till wait on
 * it would put back the round trip the whole feature removes.
 */
async function catchUp(): Promise<void> {
  try {
    if (await catalog.revalidate(fetchSnapshot)) fanout?.(['products', 'categories', 'settings'])
  } catch {
    // Offline. What is on disk is still the best answer we have, and the SSE
    // watchdog will try again within the minute.
  }
}

/**
 * Sign-out. Drops the catalog — it belongs to a store this browser is leaving —
 * but KEEPS the queue: a sale that has been paid for is not something to throw away
 * because someone ended their shift. It is namespaced per user, so it simply waits
 * for that cashier to sign in again.
 *
 * The reserved receipt block stays for the same reason. Those numbers are already
 * spent as far as the server's counter is concerned, so discarding them would burn
 * a block on every sign-out; kept, the same cashier picks up where they left off.
 */
/**
 * Head office switched branch. Same store, same user, so the namespace and queue are
 * untouched — but every cached quantity is now the wrong branch's, and a locally built
 * receipt would name the old one. Take the whole snapshot again (a delta keyed on the
 * cursor would return nothing: no product row changed on the server).
 */
export async function observeActingLocation(next: SessionUser): Promise<void> {
  session = next
  details.clear()
  try {
    await catalog.refresh(fetchSnapshot)
  } catch {
    // Offline: the stale numbers stay until the SSE watchdog revalidates.
  }
  fanout?.(['products', 'categories', 'settings'])
}

export async function forgetSession(): Promise<void> {
  session = null
  cashSession = null
  drawerKnown = false
  details.clear()
  await catalog.unbind()
}

// ---------- reads ----------

/** The catalog with unpushed sales subtracted. See applyPendingStock for why. */
function withPending(state: CatalogState): CatalogState {
  const n = ns()
  return n ? applyPendingStock(state, queue.pendingDeltas(n)) : state
}

/**
 * The cache's view, warming it first if this is the cold start.
 *
 * Never throws. A cache that cannot answer must look exactly like a cache miss —
 * if a failed `catalog:sync` escaped from here it would surface as the answer to
 * whatever read the caller actually made, so a FORBIDDEN on the snapshot would be
 * reported as a FORBIDDEN on `products:list`.
 */
async function state(): Promise<CatalogState | null> {
  if (!catalog.boundStore()) return null
  try {
    const s = catalog.peek() ?? (await catalog.ensure(fetchSnapshot))
    return s ? withPending(s) : null
  } catch {
    return null
  }
}

function detailFrom(cached: ProductDetail, s: CatalogState): ProductDetail {
  const base = s.products.find((p) => p.id === cached.id)
  const variants: ProductVariant[] | undefined = s.variants[cached.id]
  return {
    ...cached,
    // Price, stock and active flags come from the delta-synced catalog; description,
    // images, options, attributes and tags stay as the server last described them.
    ...(base ?? {}),
    variants: variants ?? cached.variants
  }
}

/**
 * Answer a read from cache, or say we can't.
 *
 * Returning `undefined` means "not handled" — the caller falls through to the
 * network, which is also what happens on the very first call before anything is
 * cached, and if the cache layer is disabled or broken.
 */
export async function readThrough(channel: string, req: unknown): Promise<unknown | undefined> {
  if (!CACHED_READS.has(channel)) return undefined
  const s = await state()
  if (!s) return undefined

  switch (channel) {
    case 'products:list': {
      const opts = (req ?? {}) as { search?: string; includeInactive?: boolean }
      return filterProducts(s.products, opts)
    }
    case 'products:get': {
      const { productId } = req as { productId: string }
      const cached = details.get(productId)
      return cached ? detailFrom(cached, s) : undefined
    }
    case 'categories:list':
      return filterCategories(s.categories, (req ?? {}) as { includeInactive?: boolean })
    case 'settings:get':
      return s.settings
    case 'storeProfile:get':
      return s.storeProfile
    case 'locations:list':
      // A cache written before branches existed has none; let the server answer once.
      return s.locations ?? undefined
    default:
      return undefined
  }
}

/** Remember the open drawer; anything not open (closed, or none) forgets it. */
export function rememberCashSession(s: CashSession | CashSessionDetail | null): void {
  cashSession = s && s.status === 'open' ? { ...s } : null
  drawerKnown = true
}
export function cachedCashSession(): CashSession | null {
  return cashSession
}

/** Remember what the server said, so the next identical read is free. */
export function observeResponse(channel: string, res: unknown): void {
  if (
    channel === 'cash:current' ||
    channel === 'cash:open' ||
    channel === 'cash:move' ||
    channel === 'cash:close'
  ) {
    rememberCashSession(res as CashSession | CashSessionDetail | null)
    return
  }
  if (channel === 'products:get') {
    const detail = res as ProductDetail
    if (detail?.id) details.set(detail.id, detail)
  }
}

/**
 * Every read channel under `products:` and `categories:`. Enumerated rather than
 * pattern-matched, because getting this wrong is silent: `products:get` matches a
 * `products:` prefix, so a naive prefix test would clear the detail cache on the
 * very call that just filled it, and the variant picker would pay a round trip
 * every single time it opened.
 */
const CATALOG_READ_CHANNELS = new Set<string>([
  'products:list',
  'products:get',
  'products:costLogs',
  'categories:list',
  'categories:listAttributes'
])

/**
 * A write went through. Drop the ProductDetail responses, which nothing else
 * refreshes — price, stock and the variant list are overlaid from the catalog, but
 * description, images, options and tags are only ever what the server last said.
 *
 * Coarse on purpose: these are manager actions on the Inventory screen, not the
 * till hot path, so being exact would buy nothing.
 *
 * Only writes from THIS browser. Another till editing a description leaves a stale
 * one here until the tab is reloaded — accepted, because the alternative is
 * clearing on the `products` topic, which fires on every sale in the shop and would
 * put the variant-picker round trip straight back.
 */
export function observeMutation(channel: string): void {
  if (CATALOG_READ_CHANNELS.has(channel)) return
  if (channel.startsWith('products:') || channel.startsWith('categories:')) details.clear()
}

/**
 * SSE said something changed. Refresh the cache BEFORE the renderer is told, so the
 * refetch every screen is about to do is served from fresh data rather than racing
 * the revalidate.
 */
export async function onServerTopics(topics: DataTopic[]): Promise<void> {
  if (!catalog.boundStore()) return
  if (!topics.some((t) => CATALOG_TOPICS.has(t))) return
  try {
    await catalog.revalidate(fetchSnapshot)
  } catch {
    // Offline, or the session lapsed. The cache keeps serving what it has, which is
    // the entire point; the next topic or the SSE watchdog will try again.
  }
}

// ---------- charge ----------

function lookupFor(s: CatalogState): CatalogLookup {
  return {
    product: (id) => s.products.find((p) => p.id === id),
    variant: (productId, variantId) =>
      (s.variants[productId] ?? []).find((v) => v.id === variantId)
  }
}

function failure(code: string, message: string): Error & { code: string } {
  const err = new Error(message) as Error & { code: string }
  err.code = code
  return err
}

async function topUpReceipts(): Promise<void> {
  const n = ns()
  if (!n || !post) return
  if (queue.receiptsLeft(n) >= RECEIPT_REFILL_AT) return
  try {
    const block = await post('sales:reserveReceipts', { count: RECEIPT_BLOCK })
    await queue.addReceipts(n, block.numbers)
  } catch {
    // No connection, or no licence. Charge falls back to the synchronous path and
    // reports the real error there, which is the right place for the cashier to
    // see it.
  }
}

/**
 * Ring up a sale without touching the network.
 *
 * Returns `undefined` when this cart is not eligible, and the caller posts it the
 * old way. That is not a fallback so much as the design: a cart carrying a price
 * override needs a supervisor PIN, and only the server can check one. Booking a
 * sale on an unverified PIN would quietly delete a control the shop turned on, so
 * those carts keep waiting for the round trip. They are rare.
 */
export async function chargeLocally(input: CheckoutInput): Promise<SaleDetail | undefined> {
  const n = ns()
  if (!n || !session) return undefined
  if (input.supervisorPin || input.items.some((i) => i.priceOverrideCents != null)) return undefined

  const s = await state()
  if (!s) return undefined
  // "Require online sales": the shop wants every sale on the server before the
  // receipt prints. Falling through takes the synchronous round trip, and offline
  // that fails visibly (NETWORK) instead of quietly queueing.
  if (s.settings.requireOnlineSales) return undefined
  // Drawer state not learned yet (no cash:* response this session): don't guess which drawer — or
  // that there is none — take the round trip and let the server stamp it.
  if (input.paymentMethod === 'cash' && !drawerKnown) return undefined
  // "Cash session required": with no open drawer known here, take the round trip so the
  // server's CASH_SESSION_REQUIRED reaches the cashier BEFORE any money is taken.
  if (input.paymentMethod === 'cash' && s.settings.cashSessionRequired && !cashSession) {
    return undefined
  }
  // Captured with the other module state: the replay lands in the drawer that was open
  // when the money was taken, not whichever is open when the queue drains.
  const drawerId = input.paymentMethod === 'cash' ? (cashSession?.id ?? null) : null

  // Throws for a cart that is wrong — an unknown product, a missing serial, a
  // discount bigger than the total — exactly as the server would, and with the same
  // message, because it is the same function.
  const priced = priceCart(input, lookupFor(s), {
    registered: s.settings.vatRegistered,
    rateBp: s.settings.vatRateBp,
    inclusive: s.settings.pricesIncludeVat
  })
  // And refuses to oversell. "The cloud always accepts" is about a queued sale that
  // LOST A RACE to another till while it waited, not about letting a cashier sell
  // past stock the screen in front of them already shows as gone.
  assertStock(priced.lines)

  const receiptNumber = await queue.takeReceipt(n)
  if (!receiptNumber) {
    void topUpReceipts()
    return undefined
  }

  const occurredAt = new Date().toISOString()
  const saleId = crypto.randomUUID()
  // Captured before the awaits below: `session` is module state and could be swapped
  // by a sign-out mid-charge (the guard at the top already proved it non-null).
  const acting = session.actingLocationId
  const items: SaleItemDetail[] = priced.lines.map((line) => ({
    // The server mints its own sale_items ids. These are local stand-ins for React
    // keys and the receipt; once the sale lands, anything that re-reads it from the
    // server gets the real ones.
    id: crypto.randomUUID(),
    productId: line.productId,
    variantId: line.variantId,
    productName: line.productName,
    variantLabel: line.variantId
      ? ((s.variants[line.productId] ?? []).find((v) => v.id === line.variantId)?.label ?? null)
      : null,
    quantity: line.quantity,
    sellingPriceCents: line.priceCents,
    landedCostCents: line.costCents,
    priceOverridden: line.overridden,
    returnedQuantity: 0,
    warrantyMonths: line.warrantyMonths,
    lineNote: line.lineNote,
    serials: line.serials.map((value, i) => ({ value, condition: line.conditions[i] ?? null }))
  }))

  const sale: SaleDetail = {
    id: saleId,
    receiptNumber,
    cashierName: session.fullName,
    locationId: acting,
    locationName: (s.locations ?? []).find((l) => l.id === acting)?.name ?? null,
    totalCents: priced.totalCents,
    profitCents: priced.totalCents - priced.cogsCents,
    paymentMethod: input.paymentMethod,
    createdAt: occurredAt,
    returnCount: 0,
    subtotalCents: priced.subtotalCents,
    discountCents: priced.discountCents,
    vatCents: priced.vatCents,
    cogsCents: priced.cogsCents,
    customerName: input.customerName?.trim() || null,
    customerPhone: input.customerPhone?.trim() || null,
    items
  }

  await queue.enqueue({
    id: saleId,
    ns: n,
    input: {
      ...input,
      saleId,
      receiptNumber,
      occurredAt,
      source: 'queued',
      cashSessionId: drawerId
    },
    sale,
    deltas: priced.lines.map((l) => ({
      productId: l.productId,
      variantId: l.variantId,
      quantity: l.quantity
    })),
    queuedAt: occurredAt,
    attempts: 0,
    state: 'pending',
    lastError: null
  })

  drain?.kick()
  void topUpReceipts()
  return sale
}

// ---------- status ----------

/**
 * The badge in the sidebar. This shim used to fabricate a permanently-idle status
 * because there was nothing to sync; now there is, and the component that renders
 * it needs no change at all.
 */
export function status(): SyncStatus {
  const n = ns()
  if (!n) return { state: 'idle', lastSyncAt: null, pendingCount: 0, error: null }
  const { pending, failed } = queue.counts(n)
  if (failed > 0) {
    return {
      state: 'error',
      lastSyncAt: null,
      pendingCount: pending + failed,
      error: `${failed} sale${failed === 1 ? '' : 's'} could not be saved to the cloud`
    }
  }
  if (pending > 0) {
    return { state: 'syncing', lastSyncAt: null, pendingCount: pending, error: null }
  }
  return { state: 'idle', lastSyncAt: new Date().toISOString(), pendingCount: 0, error: null }
}

export function subscribeStatus(cb: () => void): () => void {
  return queue.subscribe(cb)
}

// ---------- installation ----------

export function install(transport: Post, announce: (topics: DataTopic[]) => void): void {
  post = transport
  fanout = announce
  drain = startDrain({
    ns,
    async push(sale) {
      if (!post) throw failure('NETWORK', 'Offline')
      await post('sales:checkout', sale.input)
      // The server now owns this sale's stock, so the catalog it hands back next
      // already includes it — and the overlay entry disappears with the queue row.
      // Tell the screens, or the till shows the pending number until something else
      // happens to move.
      fanout?.(['sales', 'products'])
    }
  })

  // A queue survives a reload, and nobody has signed in yet at this point — so load
  // it eagerly, or a sale rung up before a refresh sits there until the next one.
  void queue.load()

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', (e) => {
      const n = ns()
      if (!n) return
      const { pending, failed } = queue.counts(n)
      if (pending + failed === 0) return
      // Closing the tab now strands sales that exist nowhere else. The browser shows
      // its own wording; the only thing that matters is that it asks at all.
      e.preventDefault()
      e.returnValue = ''
    })
  }
}

/** Test seam. */
export function _reset(): void {
  post = null
  session = null
  cashSession = null
  drawerKnown = false
  fanout = null
  drain?.stop()
  drain = null
  details.clear()
  catalog._reset()
  queue._reset()
}
